import imageCompression, { Options } from 'browser-image-compression';

export interface CompressResult {
  file: File;
  originalSize: number;
  compressedSize: number;
  ratio: number;
}

/**
 * Compresse une image (JPEG/PNG/WebP) côté navigateur avant upload.
 * PDF et fichiers non-image sont retournés tels quels.
 *
 * Défauts : max 1 Mo, 1920px de côté, WebWorker si dispo — largement
 * suffisant pour un reçu ou une photo de profil, tout en restant lisible
 * pour la validation manuelle par la scolarité.
 */
export async function compressImage(
  file: File,
  overrides: Partial<Options> = {},
): Promise<CompressResult> {
  const originalSize = file.size;
  if (!file.type.startsWith('image/')) {
    return { file, originalSize, compressedSize: originalSize, ratio: 1 };
  }
  const opts: Options = {
    maxSizeMB: 1,
    maxWidthOrHeight: 1920,
    useWebWorker: true,
    initialQuality: 0.8,
    fileType: file.type === 'image/png' ? 'image/png' : 'image/jpeg',
    ...overrides,
  };
  try {
    const compressed = await imageCompression(file, opts);
    // browser-image-compression renvoie un Blob typé File avec `name` — mais
    // sur certains navigateurs le name est perdu ; on le restaure.
    const outFile = compressed instanceof File
      ? compressed
      : new File([compressed], file.name, { type: opts.fileType || file.type });
    return {
      file: outFile,
      originalSize,
      compressedSize: outFile.size,
      ratio: originalSize > 0 ? outFile.size / originalSize : 1,
    };
  } catch {
    // En cas d'échec (image corrompue, formats exotiques), on renvoie
    // l'original — l'utilisateur préfère un upload lourd à une erreur.
    return { file, originalSize, compressedSize: originalSize, ratio: 1 };
  }
}

export function formatBytes(n: number): string {
  if (n < 1024) return `${n} o`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} Ko`;
  return `${(n / 1024 / 1024).toFixed(1)} Mo`;
}
